// ─────────────────────────────────────────────────────────────────────────────
// Instances HTTP configurées — une par backend.
//   apiClient       → backend BI (.NET)        : auth, cube/MDX, users, soldes…
//   aiClient        → service IA / prévision (Flask) : /prevision-f/*
//   avsoftClient    → backend Avsoft (ERP)      : OTP, pièces jointes
//                     URL propre à chaque client, lue via GET /auth/config
//
// Les composants n'utilisent pas ces clients directement : ils passent par
// les fonctions typées de ./endpoints.
// ─────────────────────────────────────────────────────────────────────────────
import { env } from "../config/env";
import { createHttpClient } from "./http";
import { session } from "./session";

let unauthorizedHandler: (() => void) | null = null;

/** Enregistré par l'App : appelé quand une requête authentifiée reçoit un 401. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

const authConfig = {
  getToken: () => session.getToken(),
  onUnauthorized: () => unauthorizedHandler?.(),
};

export const apiClient = createHttpClient({ baseUrl: env.backendApiUrl, ...authConfig });

export const aiClient = createHttpClient({
  baseUrl: env.aiApiUrl,
  ...authConfig,
});

// ── Configuration runtime du client (table parametre_bi) ─────────────────────

export interface AuthConfig {
  /** URL racine du backend Avsoft, avec ou sans /api final. */
  url_backend_bi?: string;
  nom_cube?: string;
}

let authConfigPromise: Promise<AuthConfig> | null = null;

/** GET /auth/config — mis en cache ; un échec n'est pas mis en cache. */
export function loadAuthConfig(): Promise<AuthConfig> {
  if (!authConfigPromise) {
    authConfigPromise = apiClient
      .get<AuthConfig>("/auth/config", { auth: false, timeoutMs: 10_000 })
      .then(({ data }) => data ?? {})
      .catch((err) => {
        authConfigPromise = null;
        throw err;
      });
  }
  return authConfigPromise;
}

async function resolveAvsoftBaseUrl(): Promise<string> {
  const { url_backend_bi } = await loadAuthConfig();
  const base = (url_backend_bi ?? "").trim().replace(/\/+$/, "");
  if (!base) {
    throw new Error("url_backend_bi n'est pas configuré (table parametre_bi).");
  }
  return /\/api$/i.test(base) ? base : `${base}/api`;
}

export const avsoftClient = createHttpClient({
  baseUrl: resolveAvsoftBaseUrl,
  ...authConfig,
});

export default apiClient;
